import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { useNetworthHistory } from "../../hooks/networth";
import { formatEur } from "../../lib/format-kpi";
import { type HistoryRow, findYoyMatch } from "../../lib/networth-utils";
import { NetworthChartFullscreen } from "./NetworthChartFullscreen";

interface Props {
  onDatapointClick?: (stichtag: string) => void;
}

export function NetworthChartCard({ onDatapointClick }: Props) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const query = useNetworthHistory("1J");
  const rawData: HistoryRow[] = query.data?.history ?? [];
  const data = rawData.filter((r) => r.totalNetCents !== 0);

  const last = data[data.length - 1];
  const first = data[0];
  const totalCents = last?.totalNetCents ?? 0;

  const yoyMatch = last ? findYoyMatch(data, last.stichtag, 7) : null;
  const yoyValue = yoyMatch?.totalNetCents ?? null;
  const delta = yoyValue !== null ? totalCents - yoyValue : null;
  const deltaPct =
    yoyValue !== null && yoyValue !== 0 && delta !== null ? (delta / yoyValue) * 100 : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="fv-card p-4 w-full text-left active:opacity-80 transition-opacity"
        aria-label={t("dashboard.networth_chart.open_fullscreen", {
          defaultValue: "Vermögensverlauf vergrößern",
        })}
      >
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-base font-semibold">
            {t("dashboard.networth_chart.title", { defaultValue: "Vermögensverlauf" })}
          </h2>
          <svg
            className="w-5 h-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "var(--muted)" }}
            aria-hidden="true"
          >
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
        <div className="num text-2xl font-semibold">{formatEur(totalCents)}</div>
        {delta !== null && deltaPct !== null && (
          <div
            className="text-sm font-medium mt-1"
            style={{ color: delta >= 0 ? "var(--pos)" : "var(--neg)" }}
          >
            {delta >= 0 ? "+" : ""}
            {formatEur(delta)} ({deltaPct >= 0 ? "+" : ""}
            {deltaPct.toFixed(1)} %)
          </div>
        )}
        {data.length >= 2 && (
          <div style={{ width: "100%", height: 60 }} className="mt-3">
            <ResponsiveContainer>
              <LineChart data={data}>
                <Line
                  type="monotone"
                  dataKey="totalNetCents"
                  stroke="var(--accent)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {first && last && (
          <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>
            {first.stichtag.slice(8, 10)}.{first.stichtag.slice(5, 7)}.{first.stichtag.slice(0, 4)}{" "}
            — {last.stichtag.slice(8, 10)}.{last.stichtag.slice(5, 7)}.{last.stichtag.slice(0, 4)}
          </div>
        )}
      </button>
      <NetworthChartFullscreen
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDatapointClick={onDatapointClick}
      />
    </>
  );
}
